// External library imports
import * as yup from 'yup';
import _ from 'lodash';
// Internal library imports
import { ERRORS } from '../../utils/validationHelper';

const approvedValidityShape = (market = {}) => {
    const shape = {};
    for (let m in market) {
        shape[m] = yup.object().shape({
            startDate: yup.string().test('start date', 'Start date should be less than end date', (value) => {
                const endDate = _.get(market, [m, 'endDate']);
                if (endDate && new Date(endDate) < new Date(value)) {
                    return false;
                } else {
                    return true;
                }
            }),
            endDate: yup.string().test('end date', 'End date should be greater than start date', (value) => {
                const startDate = _.get(market, [m, 'startDate']);
                if (startDate && new Date(startDate) > new Date(value)) {
                    return false;
                } else {
                    return true;
                }
            }),
        });
    }

    return yup.object().shape(shape);
};

// Map field names to their corresponding Yup validators
const metaFieldValidators = {
    status: yup.boolean().required(),
    connectors: yup.number().required(),
    connectorDetails: yup.array().when('connectors', {
        is: (val) => val > 0,
        then: yup
            .array()
            .of(
                yup.object().shape({
                    connectorId: yup.string().required().trim(),
                    maxOutputKW: yup.number().required().min(0),
                    minOutputKW: yup.number().required().min(0),
                })
            )
            .required()
            .min(yup.ref('connectors'))
            .max(yup.ref('connectors')),
        otherwise: yup.array().notRequired(),
    }),
    sendOCPIChargingProfile: yup.boolean().required(),

    // BESS 2 keys
    minSOC: yup.number().required(),
    maxSOC: yup.number().required(),
    autoCharging: yup.boolean().required(),
    autoAvailability: yup.boolean().required(),
    chargingTime: yup.number().required(), // in menutes
    batteryStorageCapacity: yup.number().required(),
    autoPurchasePower: yup.boolean().required(),
    batteryChargingPowerKW: yup.number().required(),
    batteryMaxChargingPowerKW: yup.number().required(),
    linkedInstance: yup.array().of(
        yup
            .string()
            .optional()
            .matches(/^[0-9a-fA-F]{24}$/, 'Invalid Instance ID')
    ),
};

const inventoryValueValidator = (market = {}) => {
    const shape = {};
    for (let m in market) {
        shape[m] = yup.number().optional().nullable();
    }
    return yup.object().shape(shape);
};

const dailyChargingScheduleValidator = (context, daysForSchedule, licenseName) => {
    const { meta } = context?.parent || {};

    const dailyChargingScheduleValidations = daysForSchedule.reduce((acc, day) => {
        acc[day] = yup.object().shape({
            pluginTime: yup.string().required(ERRORS.required),
            plugoutTime: yup.string().required(ERRORS.required),
        });
        return acc;
    }, {});

    const shouldValidate = licenseName === 'smartCharging' && _.get(meta, 'sendOCPIChargingProfile', false) === true;

    if (!shouldValidate) {
        return yup.mixed().optional();
    }

    return yup.object().shape(dailyChargingScheduleValidations);
};

export const instanceFormValidation = (defaultMetaKeys, daysForSchedule, licenseName) => {
    const metaValidationObject = {};
    _.keys(defaultMetaKeys).forEach((item) => {
        metaValidationObject[item] = metaFieldValidators[item] || yup.string().required();
    });

    return yup.object().shape({
        name: yup.string().required(ERRORS.required),
        description: yup.string().optional(),
        licenseId: yup.string().required(ERRORS.required),
        market: yup.array().min(1, "This field can't be blank.").required("This field can't be blank.").max(3, 'Can not select more than 3 markets.'),
        metaErrorKeys: yup.object().shape(metaValidationObject),
        meta: yup.mixed(),
        inMarket: yup.boolean().required(),
        inLedger: yup.boolean().required(),
        approvedCapacity: yup.mixed().optional(),
        webshopUrl: yup.array().optional(),
        jiraLink: yup.string().optional(),
        address: yup.object().shape({
            floor: yup.string().optional().nullable(),
            streetName: yup.string().optional().nullable(),
            city: yup.string().optional().nullable(),
            pincode: yup.number().optional().nullable(),
            country: yup.string().optional().nullable(),
        }),
        approvalValidity: yup.lazy((obj) => {
            return approvedValidityShape(obj);
        }),
        inventoryValue: yup.lazy((el) => {
            return inventoryValueValidator(el);
        }),
        geoTag: yup.string().when('market', {
            is: (market) => {
                let isRequired = false;
                market.map((m) => {
                    if (m?.value?.includes('mFRR')) isRequired = true;
                });
                return isRequired;
            },
            then: yup.string().required(),
            otherwise: yup.string().optional(),
        }),
        datahubMeters: yup.string().optional(),
        fuelType: yup.string().when('market', {
            is: (market) => {
                let isRequired = false;
                market.map((m) => {
                    if (m?.value?.includes('aFRR') || m?.value?.includes('mFRR')) isRequired = true;
                });
                return isRequired;
            },
            then: yup.string().required(),
            otherwise: yup.string().optional().nullable(),
        }),
        dailyChargingSchedule: yup.lazy((_, context) => {
            return dailyChargingScheduleValidator(context, daysForSchedule, licenseName);
        }),
        area: yup.string().required(),
        resourceSchedule: yup.boolean().optional(),
        energyMarketOnly: yup.boolean().required(),
        // yup.string().when('market', {
        //     is: (market) => {
        //         let isRequired = false;
        //         market.map((m) => {
        //             if (AFRR_MARKET_TYPES.includes(m.value)) {
        //                 isRequired = true;
        //             }
        //         });
        //         return isRequired;
        //     },
        //     then: yup.string().optional(),
        //     otherwise: yup.string().optional(),
        // }),
    });
};
